import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FaTrash, FaPlus, FaCheck, FaTimes, FaNewspaper, FaEye, FaSearch } from 'react-icons/fa';
import '../pages_premium.css';
import './BlogsList.css';
import AdminPagination from '../components/common/AdminPagination';

function BlogsList() {
    const [blogs, setBlogs] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchBlogs = async () => {
        try {
            const res = await axiosInstance.get('/blogs');
            setBlogs(res.data);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            toast.error('Failed to fetch blogs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this blog?")) {
            try {
                await axiosInstance.delete(`/blogs/${id}`);
                toast.success('✍️ Blog deleted successfully');
                fetchBlogs();
            } catch (error) {
                console.error('Error deleting blog:', error);
                toast.error('Failed to delete blog');
            }
        }
    };

    const filtered = blogs.filter(b => 
        b.title?.toLowerCase().includes(search.toLowerCase()) ||
        b.content?.toLowerCase().includes(search.toLowerCase()) ||
        b.Author?.name?.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const paginatedBlogs = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const publishedCount = blogs.filter(b => b.status === 'published').length;
    const draftCount = blogs.filter(b => b.status === 'draft').length;

    return (
        <div className="blogs-list-page page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header">
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaNewspaper />
                        </div>
                        <div>
                            <h1 className="ph-title">Blogs &amp; Guides</h1>
                            <p className="ph-subtitle">Manage, publish, and delete student news and guides</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <Link to="/blogs/add" className="add-blog-btn">
                            <FaPlus /> Add New Blog
                        </Link>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="row mb-4">
                    <div className="col-md-4 mb-3">
                        <div className="stat-card-mini">
                            <div className="stat-card-label">Total Articles</div>
                            <div className="stat-card-val">{blogs.length}</div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="stat-card-mini">
                            <div className="stat-card-label">Published</div>
                            <div className="stat-card-val" style={{ color: '#16a34a' }}>{publishedCount}</div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="stat-card-mini">
                            <div className="stat-card-label">Drafts</div>
                            <div className="stat-card-val" style={{ color: '#d97706' }}>{draftCount}</div>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Articles List
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search articles by title, author..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Title &amp; Summary</th>
                                    <th>Author</th>
                                    <th>Status</th>
                                    <th className="text-center" style={{ width: '120px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5 text-muted">
                                             No articles or blogs found. Click &quot;Add New Blog&quot; to publish one!
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedBlogs.map((blog) => (
                                        <tr key={blog.id}>
                                            <td style={{ color: '#64748b', fontWeight: 600 }}>#{blog.id}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{blog.title}</span>
                                                    <span style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px', maxWidth: '500px', lineHeight: '1.4' }}>
                                                        {(() => {
                                                            if (!blog.content) return '';
                                                            const cleanText = blog.content
                                                                .replace(/<[^>]*>?/gm, '')
                                                                .replace(/[#*_~`[\]()]/g, '')
                                                                .replace(/\s+/g, ' ')
                                                                .trim();
                                                            return cleanText.length <= 110 ? cleanText : `${cleanText.substring(0, 110)}...`;
                                                        })()}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: 600, color: '#334155' }}>
                                                    {blog.Author ? blog.Author.name : 'Administrator'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`ptag ${blog.status === 'published' ? 'ptag-active' : 'ptag-banned'}`}>
                                                    {blog.status === 'published' ? <FaCheck style={{ marginRight: 4 }} /> : <FaTimes style={{ marginRight: 4 }} />}
                                                    {blog.status}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="action-btn-group">
                                                    <Link 
                                                        to={`/blogs/${blog.id}`}
                                                        className="icon-action-btn icon-action-btn-view"
                                                        title="View Blog"
                                                    >
                                                        <FaEye />
                                                    </Link>
                                                    <button 
                                                        className="icon-action-btn icon-action-btn-delete"
                                                        onClick={() => handleDelete(blog.id)}
                                                        title="Delete Blog"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="articles"
                    />
                </div>

            </div>
        </div>
    );
}

export default BlogsList;
